import React, { useEffect } from "react";
import {SessionProvider} from "next-auth/react";
import Head from "next/head";
import Router, { useRouter } from "next/router";
import Script from 'next/script';
import "../style.css";
import '../honeybadger.browser.config'
import { Honeybadger, HoneybadgerErrorBoundary } from '@honeybadger-io/react'
import NotificationsProvider, {NotificationsContext} from "../components/NotificationsProvider";
import {DialogProvider} from "../components/DialogProvider";
import DataTablePreferenceProvider from "../components/DataTablePreferenceProvider";
import HideScrollBar from "../components/styledwrappers/HideScrollBar";
import ThemeProvider from "../components/ThemeProvider";
import AuthManager from "../components/hoc/AuthManager"
import RouteProgressBar from "../components/RouteProgressBar";
import * as snippet from '@segment/snippet';
import { pageEvent } from "../utils/segment";
import { config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
config.autoAddCss = false

// This is handy for testing in dev. Will not be available in prod
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    window.Honeybadger = Honeybadger
}

// Handle chunk load errors by reloading the page (happens after deployments)
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        const message = event?.reason?.message || '';
        if (message.includes('Failed to load chunk') || message.includes('Loading chunk')) {
            // Only reload once to prevent infinite loops
            const lastReload = sessionStorage.getItem('chunk_error_reload');
            const now = Date.now();

            // Allow reload if never reloaded, or if last reload was more than 10 seconds ago
            if (!lastReload || (now - parseInt(lastReload, 10)) > 10000) {
                sessionStorage.setItem('chunk_error_reload', now.toString());
                window.location.reload();
            }
            // If we already reloaded recently, let the error propagate to Honeybadger
        }
    });
}

// This is to track the previous url to ensure our back button (router.back()) won't navigate user
// outside of our app
const StoreBackUrl = () => {
    const router = useRouter();
    useEffect(() => {
        if (typeof globalThis == "undefined" || !globalThis) return;
        const storage = globalThis?.sessionStorage;
        if (!storage) return;
        storage.setItem("prevPath", storage.getItem("currentPath"));
        storage.setItem("currentPath", globalThis.location.pathname);
    }, [router.asPath]);
    return (<></>);
}

const renderSegmentSnippet = () => {
    const opts = {
        apiKey: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY || '',
        // note: the page option only covers SSR tracking.
        // Router.events will cover the client side page changes
        page: false,
    };

    if (process.env.NODE_ENV === 'development') return snippet.max(opts);

    return snippet.min(opts);
};

Router.events.on('routeChangeComplete', (url) => {
    pageEvent(url);
});


function MyApp({ Component, pageProps, initialTheme }) {
    const { session } = pageProps || {};
    return (
        <HoneybadgerErrorBoundary honeybadger={Honeybadger}>
            <SessionProvider session={session} refetchInterval={60*30}>
                <Head>
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable=0"
                    />
                </Head>
                <Script
                    id="segment-script"
                    dangerouslySetInnerHTML={{ __html: renderSegmentSnippet() }}
                />
                <AuthManager isAuthRequired={Component.auth !== false}/>
                <StoreBackUrl />
                <NotificationsProvider>
                    <DialogProvider>
                        <DataTablePreferenceProvider>
                            <ThemeProvider initialTheme={initialTheme}>
                                {/* Inside the theme and session providers: it reads the
                                    merchant's brand colour from both. */}
                                <RouteProgressBar />
                                <HideScrollBar>
                                    <NotificationsContext.Consumer>
                                        {(notificationProps) => (
                                            <Component {...pageProps} {...notificationProps}/>
                                        )}
                                    </NotificationsContext.Consumer>
                                </HideScrollBar>
                            </ThemeProvider>
                        </DataTablePreferenceProvider>
                    </DialogProvider>
                </NotificationsProvider>
            </SessionProvider>
        </HoneybadgerErrorBoundary>
    );
}

export default MyApp;
